import React from 'react';
import styled, { css } from 'styled-components';
import { Typography } from '../typography';

export enum AlertType {
  ERROR = 'error',
  WARNING = 'warning',
  SUCCESS = 'success',
  INFO = 'info',
  EXPIRED = 'expired'
}

export interface IProps {
  children?: React.ReactNode;
  type: AlertType;
  label?: React.ReactNode;
  additionalInlineContent?: React.ReactElement;
  large?: boolean;
  iconLabelGap?: string;
  padding?: string;
}

export const Alert = ({
  type,
  label,
  children,
  className,
  large = false,
  additionalInlineContent,
  iconLabelGap,
  padding
}: IProps & { className?: string }) => (
  <AlertWrapper className={className} type={type} large={large} padding={padding}>
    {children && <AlertIcon marginRight={iconLabelGap || '2rem'}>{children}</AlertIcon>}
    {label && (
      <AlertLabel type={type} large={large}>
        <span>{label}</span>
      </AlertLabel>
    )}
    {additionalInlineContent}
  </AlertWrapper>
);

export const AlertWrapper = styled.div<{ padding?: string; type: string; large: boolean }>`
  ${({ padding, type, theme }) => css`
    align-items: center;
    background-color: ${theme.colors.status[type]};
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.status[type]};
    display: flex;
    justify-content: center;
    padding: ${padding || '1.6rem 2rem'};
  `};
  ${({ padding, large, theme }) =>
    large &&
    css`
      border-radius: ${theme.borderRadius.xlarge};
      padding: ${padding || '2rem 0'};
    `};
`;

export const AlertIcon = styled.span<{ marginRight: string }>`
  display: inherit;
  margin-right: ${({ marginRight }) => marginRight};
`;
AlertIcon.displayName = 'AlertIcon';

export const AlertLabel = styled(Typography).attrs<{ large?: boolean }>(({ large }) => ({
  as: 'p',
  size: large ? '2xl' : 'md',
  weight: large ? 700 : 400
}))<{
  type: AlertType;
  large: boolean;
}>`
  ${({ type, theme }) => css`
    color: ${[AlertType.ERROR, AlertType.EXPIRED].includes(type)
      ? theme.colors.white
      : theme.components.global.textColor};
    margin: 0;
    text-align: center;
  `};
`;
